import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import './EntryFlowModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

/**
 * Verifiko → Ruaj → Vazhdo — the shared post-calculation dialog.
 *
 * Phase 'verify': calculation summary (+ scanned document when present);
 * "Ruaj" saves, X / scrim / Esc cancels without saving.
 * Phase 'success': "U ruajt me sukses" + coverage chips for the month +
 * "Shto aktivitet të ri" / "Mbaroj" (the data-scientist flow).
 */
export const EntryFlowModal = ({
  open,
  phase,            // 'verify' | 'success'
  onClose,
  rows = [],        // [{ label, value }]
  result,           // { value, unit }
  note,             // optional string under the result (e.g. biogenic memo)
  extraRows = [],   // optional secondary list (e.g. commuting survey modes)
  doc,              // { url, type: 'image'|'pdf', name } | null
  saving = false,
  error,
  onSave,
  successText,
  coverage = [],    // [{ label, done, count, to }]
  nextStep,         // { label, to } — suggested when the whole scope is complete
  onAddAnother,
  onFinish
}) => {
  const navigate = useNavigate();
  const { t } = useT();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') (phase === 'success' ? onFinish : onClose)();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, phase, onClose, onFinish]);

  if (!open) return null;

  const handleScrim = (e) => {
    if (e.target !== e.currentTarget) return;
    (phase === 'success' ? onFinish : onClose)();
  };

  return (
    <div className="efm-scrim" onClick={handleScrim} role="dialog" aria-modal="true" aria-label={phase === 'verify' ? t('eflow.verifyTitle') : t('eflow.savedTitle')}>
      <div className="efm-modal">
        <div className="efm-head">
          <h3>{phase === 'verify' ? t('eflow.verifyTitle') : t('eflow.savedTitle')}</h3>
          <button
            type="button"
            className="efm-x"
            aria-label={phase === 'verify' ? t('eflow.closeNoSave') : t('eflow.close')}
            onClick={phase === 'success' ? onFinish : onClose}
          >
            ✕
          </button>
        </div>

        {phase === 'verify' ? (
          <div className="efm-body">
            <div className={`efm-grid ${doc ? '' : 'single'}`}>
              <div className="efm-summary">
                {rows.map((r) => (
                  <div className="efm-row" key={r.label}>
                    <span>{r.label}</span>
                    <b>{r.value}</b>
                  </div>
                ))}
                {result && (
                  <div className="efm-result">
                    {result.value} <small>{result.unit || 't CO₂e'}</small>
                  </div>
                )}
                {note && <div className="efm-note">{note}</div>}
                {extraRows.length > 0 && (
                  <div className="efm-extra">
                    {extraRows.map((r) => (
                      <div className="efm-row" key={r.label}>
                        <span>{r.label}</span>
                        <b>{r.value}</b>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {doc && (
                <div className="efm-doc">
                  <span className="efm-doc-cap">{t('eflow.attachedDoc')}{doc.name ? ` · ${doc.name}` : ''}</span>
                  {doc.type === 'pdf' ? (
                    <iframe title={t('eflow.document')} src={doc.url} className="efm-doc-frame" />
                  ) : (
                    <img alt={t('eflow.scannedDoc')} src={doc.url} className="efm-doc-img" />
                  )}
                </div>
              )}
            </div>
            {error && <div className="efm-error" role="alert">{error}</div>}
            <div className="efm-actions">
              <button type="button" className="efm-btn primary" onClick={onSave} disabled={saving}>
                {saving ? t('eflow.saving') : t('eflow.save')}
              </button>
              <button type="button" className="efm-edit" onClick={onClose}>{t('eflow.backAndFix')}</button>
            </div>
          </div>
        ) : (
          <div className="efm-body efm-success">
            <div className="efm-check" aria-hidden="true">✓</div>
            <h4>{t('eflow.savedSuccess')}</h4>
            {successText && <p>{successText}</p>}
            {coverage.length > 0 && (
              <div className="efm-coverage" aria-label={t('eflow.monthCoverage')}>
                {coverage.map((c) => (
                  <button
                    type="button"
                    key={c.label}
                    className={`efm-cov ${c.done ? 'done' : ''}`}
                    onClick={() => {
                      if (!c.done && c.to) {
                        onFinish();
                        navigate(c.to);
                      }
                    }}
                  >
                    {c.done ? '✓' : '○'} {c.label}{c.done && c.count > 1 ? ` (${c.count})` : ''}
                  </button>
                ))}
              </div>
            )}
            {nextStep && coverage.length > 0 && coverage.every((c) => c.done) && (
              <div className="efm-next">
                <span>{t('eflow.scopeComplete')}</span>
                <button
                  type="button"
                  className="efm-btn primary"
                  onClick={() => { onFinish(); navigate(nextStep.to); }}
                >
                  {t('eflow.continueTo', { label: nextStep.label })}
                </button>
              </div>
            )}
            <div className="efm-actions center">
              <button type="button" className="efm-btn primary" onClick={onAddAnother}>{t('eflow.addAnother')}</button>
              <button type="button" className="efm-btn ghost" onClick={onFinish}>{t('eflow.finish')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Fetch the month's coverage counts (shared endpoint with the notifications engine). */
export const fetchCoverage = async (period) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/emissions/coverage?period=${encodeURIComponent(period)}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/** Where the flow continues once a scope is complete for the month.
 *  Non-React module-level helper: read the stored language directly. */
export const nextStepFor = (scopeKey) => {
  const en = localStorage.getItem('k360-lang') === 'en';
  if (scopeKey === 'scope1') return { label: en ? 'Scope 2 — Electricity' : 'Scope 2 — Elektriciteti', to: '/scope-2/electricity' };
  if (scopeKey === 'scope2') return { label: en ? 'Scope 3 — Travel' : 'Scope 3 — Udhëtimet', to: '/scope-3' };
  if (scopeKey === 'scope3') return { label: en ? 'Month summary' : 'Përmbledhja e muajit', to: '/regjistrimi' };
  return null;
};

/** Coverage chips for a given screen family, from the coverage response.
 *  Non-React module-level helper: read the stored language directly. */
export const buildCoverageChips = (scopeKey, cov) => {
  if (!cov) return [];
  const en = localStorage.getItem('k360-lang') === 'en';
  if (scopeKey === 'scope1') {
    return [
      { label: en ? 'Stationary combustion' : 'Djegia stacionare', done: cov.scope1.stationary > 0, count: cov.scope1.stationary, to: '/scope-1/djegja-stacionare' },
      { label: en ? 'Mobile combustion' : 'Djegia mobile', done: cov.scope1.mobile > 0, count: cov.scope1.mobile, to: '/scope-1/djegja-mobile' },
      { label: en ? 'Processes' : 'Proceset', done: cov.scope1.process > 0, count: cov.scope1.process, to: '/scope-1/emetimet-nga-proceset' }
    ];
  }
  if (scopeKey === 'scope2') {
    return [
      { label: en ? 'Electricity' : 'Elektriciteti', done: cov.scope2.electricity > 0, count: cov.scope2.electricity, to: '/scope-2/electricity' },
      { label: en ? 'Heating' : 'Nxemja', done: cov.scope2.heating > 0, count: cov.scope2.heating, to: '/scope-2/emetimet-nga-proceset' },
      { label: en ? 'EV charging' : 'Karikimi EV', done: cov.scope2.ev > 0, count: cov.scope2.ev, to: '/scope-2/electricity' }
    ];
  }
  if (scopeKey === 'scope3') {
    return [
      { label: en ? 'Business travel' : 'Udhëtime biznesore', done: cov.scope3.business_travel > 0, count: cov.scope3.business_travel, to: '/scope-3' },
      { label: en ? 'Employee commuting' : 'Udhëtimet e punëtorëve', done: cov.scope3.employee_commuting > 0, count: cov.scope3.employee_commuting, to: '/scope-3' }
    ];
  }
  return [];
};

export default EntryFlowModal;
